/* eslint-disable eag/match-named-export */
import {Box, Center, HStack, Icon, Text, Show} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {Children, ComponentType, useRef, MouseEvent} from 'react';
import {
  components as reactSelectComponents,
  GroupBase,
  SelectComponentsConfig,
  MultiValueProps,
  SingleValueProps,
} from 'react-select';

import {isNotNil} from 'ramda';
import {isFunction} from 'ramda-adjunct';

import {noop, Nullish, suffixTestId} from 'shared/web';

import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {OptionTypeBase} from '../../../types/OptionTypeBase';
import {Checkbox} from '../../Checkbox/Checkbox';
import {IconButton} from '../../IconButton/IconButton';
import {useChoice} from '../hooks/choiceContext';
import {ChoiceInput} from './ChoiceInput';
import {StatusIndicatorDot} from './StatusIndicatorDot';

export const controlVariants = cva(
  'eag--choice__control flex h-auto min-h-0 max-h-none justify-between rounded-[var(--radius-small)] border border-solid border-[var(--color-palettes-neutral-70-100)] bg-[var(--color-palettes-white-10-100)] px-2 py-[5px] pl-2 text-[length:var(--text-size-small)] leading-[var(--text-leading-small)] font-[var(--text-weight-default)] text-[var(--color-text-primary)] hover:border-[var(--color-severity-informational)] focus:!shadow-none focus-within:!border-[var(--color-severity-informational)] focus-within:bg-[var(--color-palettes-white-10-100)] focus-within:![box-shadow:var(--shadow-active)] [&[disabled]]:pointer-events-auto [&[disabled]]:cursor-not-allowed [&[disabled]]:border-[var(--color-palettes-neutral-70-40)] [&[disabled]]:bg-[var(--color-palettes-white-10-40)] [&[disabled]]:shadow-none'
);

export const mutedTextClassName = 'text-[var(--color-text-tertiary)]';

export const valueContainerClassName = 'eag--choice__value-container gap-1';

export const indicatorsContainerClassName =
  'eag--choice__indicators-container h-[18px] !self-center';

export const menuListClassName =
  'w-full min-w-[216px] overflow-y-auto rounded-[var(--radius-medium)] border border-solid border-[#e2e8f0] bg-[var(--color-palettes-white-10-100)] p-2 text-inherit [box-shadow:var(--shadow-elevation-3)] z-[999]';

export const groupHeadingClassName =
  'mx-1 my-[2px] text-[length:var(--text-size-x-small)] leading-[var(--text-leading-x-small)] font-[var(--font-weight-regular)] tracking-[0] text-[var(--color-text-secondary)]';

export const optionContentClassName = 'pointer-events-none';

export const multiValueRemoveVariants = cva('cursor-pointer hover:opacity-80', {
  variants: {
    isDisabled: {
      true: 'pointer-events-none',
      false: 'pointer-events-auto',
    },
  },
});

export const optionCss = (isFocused: boolean, isSelected: boolean) =>
  cn(
    'eag--choice__option w-full cursor-pointer rounded-[var(--radius-small)] bg-[var(--color-palettes-white-10-100)] p-2 text-left text-[length:var(--text-size-small)] leading-[var(--text-leading-small)] font-[var(--font-weight-regular)] tracking-[0] text-[var(--color-text-primary)] hover:bg-[var(--color-palettes-blue-10-100)] hover:text-[var(--color-palettes-blue-80-100)] focus:bg-[var(--color-palettes-blue-10-100)] focus:text-[var(--color-palettes-blue-80-100)] active:bg-[var(--color-palettes-blue-10-100)] active:text-[var(--color-palettes-blue-80-100)] aria-disabled:cursor-not-allowed aria-disabled:bg-[var(--color-palettes-white-10-100)] aria-disabled:text-[var(--color-palettes-neutral-900-40)] aria-disabled:opacity-40 aria-disabled:hover:bg-[var(--color-palettes-white-10-100)] aria-disabled:hover:text-[var(--color-palettes-neutral-900-40)] aria-disabled:focus:bg-[var(--color-palettes-white-10-100)] aria-disabled:focus:text-[var(--color-palettes-neutral-80-100)] aria-disabled:active:bg-[var(--color-palettes-white-10-100)] aria-disabled:active:text-[var(--color-palettes-neutral-80-100)] [&_.option-create-new]:text-[var(--color-general-accent)] [&_svg]:size-4 [&_svg[data-iconkey^=flags/]]:h-5 [&_svg[data-iconkey^=flags/]]:w-7',
    (isFocused || isSelected) && 'bg-[var(--color-palettes-blue-10-100)]'
  );

const handleMultiValueRemoveClick = (
  event: MouseEvent<HTMLDivElement>,
  isDisabled: boolean,
  onClick: ((event: MouseEvent<HTMLDivElement>) => void) | Nullish
) => {
  event.stopPropagation();

  if (isDisabled) {
    return;
  }

  onClick?.(event);
};

export const selectComponents = <ValueType, IsMulti extends boolean>(): SelectComponentsConfig<
  OptionTypeBase<ValueType | Nullish>,
  IsMulti,
  GroupBase<OptionTypeBase<ValueType | Nullish>>
> => ({
  LoadingMessage: function LoadingMessage({children, ...props}) {
    const context = useChoice();

    return (
      <reactSelectComponents.LoadingMessage {...props}>
        <span data-testid={suffixTestId('choiceLoadingMessage', context)}>{children}</span>
      </reactSelectComponents.LoadingMessage>
    );
  },
  Control: function Control({children, innerRef, innerProps, isDisabled}) {
    const context = useChoice();
    const {css: _innerCss, ...otherProps} = {css: undefined, ...innerProps};
    return (
      <div
        className={cn(
          controlVariants(),
          context.isInvalid && '!border-[var(--color-severity-danger)]'
        )}
        data-testid={suffixTestId('choiceControl', context)}
        ref={innerRef}
        {...otherProps}
        {...(isDisabled && {disabled: true})}
      >
        {children}
      </div>
    );
  },
  Placeholder: function Placeholder({children, ...props}) {
    const context = useChoice();

    return (
      <reactSelectComponents.Placeholder
        {...props}
        data-testid={suffixTestId('choicePlaceholder', context)}
      >
        <span
          className={cn(mutedTextClassName, props.className)}
          data-testid={suffixTestId('choicePlaceholderInner', context)}
        >
          {children}
        </span>
      </reactSelectComponents.Placeholder>
    );
  },
  NoOptionsMessage: ({children, ...props}) => {
    const context = useChoice();

    return (
      <reactSelectComponents.NoOptionsMessage
        {...props}
        data-testid={suffixTestId('choiceNoOptions', context)}
      >
        <span
          className={cn(mutedTextClassName, props.className)}
          data-testid={suffixTestId('choiceNoOptionsInner', context)}
        >
          {children}
        </span>
      </reactSelectComponents.NoOptionsMessage>
    );
  },
  ValueContainer: function ValueContainer({children, ...props}) {
    const context = useChoice();

    return (
      <reactSelectComponents.ValueContainer
        {...props}
        className={cn(valueContainerClassName, props.className)}
        data-testid={suffixTestId('choiceValueContainer', context)}
      >
        {children}
      </reactSelectComponents.ValueContainer>
    );
  },
  MultiValueContainer: function MultiValueContainer({children, innerProps: _innerProps}) {
    const theme = useComponentsTheme();
    const context = useChoice();

    return (
      <Box
        minHeight={4}
        paddingLeft={1}
        maxWidth="100%"
        overflow="hidden"
        borderRadius="xSmall"
        backgroundColor={theme.components.Choice.backgroundColor}
        data-testid={suffixTestId('choiceMultiValueContainer', context)}
      >
        <HStack align="center" spacing={1}>
          {children}
        </HStack>
      </Box>
    );
  },
  IndicatorsContainer: function IndicatorsContainer({children, ...props}) {
    const context = useChoice();
    const childArray = Children.toArray(children);

    return (
      <reactSelectComponents.IndicatorsContainer
        {...props}
        data-testid={suffixTestId('choiceIndicatorContainer', context)}
        className={cn(indicatorsContainerClassName, props.className)}
      >
        {childArray}
      </reactSelectComponents.IndicatorsContainer>
    );
  },
  IndicatorSeparator: () => null,
  ClearIndicator: function ClearIndicator({innerProps}) {
    const context = useChoice();

    return (
      <div
        {...innerProps}
        data-testid={suffixTestId('choiceClearIndicatorClickableWrapper', context)}
      >
        <IconButton
          size="small"
          icon="navigation/close"
          tabIndex={-1}
          data-testid={suffixTestId('choiceClearIndicator', context)}
        />
      </div>
    );
  },
  DropdownIndicator: function DropdownIndicator({innerProps, isDisabled}) {
    const context = useChoice();

    return (
      <div
        {...innerProps}
        data-testid={suffixTestId('choiceDropdownIndicatorClickableWrapper', context)}
      >
        <IconButton
          size="small"
          icon="navigation/expand_more"
          isDisabled={isDisabled}
          tabIndex={-1}
          data-testid={suffixTestId('choiceDropdownIndicator', context)}
        />
      </div>
    );
  },
  Menu: function Menu({children, ...props}) {
    const context = useChoice();
    return (
      <reactSelectComponents.Menu
        {...props}
        data-testid={suffixTestId('choiceMenu', context)}
        className={`eag--choice__menu ${props.className}`}
      >
        {children}
      </reactSelectComponents.Menu>
    );
  },
  MenuList: function MenuList({innerRef, children, maxHeight}) {
    const context = useChoice();
    // Prevent multiple scrollIntoView calls
    const isCentered = useRef<boolean>(false);

    const handleRef = (node: HTMLDivElement | null) => {
      // Handle the function-type ref
      if (isFunction(innerRef)) {
        innerRef(node);
      }

      // Handle the object-type ref
      if (innerRef && !isFunction(innerRef) && node) {
        innerRef.current = node;
      }

      // Center the selected option to the middle of the menu
      if (node) {
        const selectedOption = node.querySelector('[data-selected="true"]');
        if (selectedOption && !isCentered.current) {
          setTimeout(() => {
            // Scroll within the container only, without scrolling the page
            const containerRect = node.getBoundingClientRect();
            const optionRect = selectedOption.getBoundingClientRect();
            const scrollTop =
              optionRect.top -
              containerRect.top +
              node.scrollTop -
              containerRect.height / 2 +
              optionRect.height / 2;
            node.scrollTop = scrollTop;
            isCentered.current = true;
          }, 0);
        }
      }
    };

    return (
      <div
        className={cn(menuListClassName)}
        style={{maxHeight}}
        ref={handleRef}
        data-testid={suffixTestId('choiceMenuList', context)}
      >
        {children}
      </div>
    );
  },
  GroupHeading: function GroupHeading({children}) {
    const context = useChoice();
    return (
      <div
        className={cn(groupHeadingClassName)}
        data-testid={suffixTestId('choiceGroupHeading', context)}
      >
        {children}
      </div>
    );
  },
  Option: function Option({
    innerRef,
    innerProps,
    children,
    isFocused,
    isDisabled,
    isSelected,
    data,
  }) {
    const context = useChoice();
    const {hasOptionCheckbox} = context;
    const {css: _innerCss, ...otherProps} = {css: undefined, ...innerProps};
    const statusIndicator = data.statusIndicator;

    return (
      <div
        className={optionCss(isFocused, isSelected)}
        ref={innerRef}
        {...otherProps}
        {...(isDisabled && {isDisabled: true})}
        data-testid={suffixTestId('choiceOption', context)}
        aria-disabled={isDisabled}
        data-selected={isSelected ? 'true' : 'false'}
        data-focused={isFocused ? 'true' : 'false'}
      >
        {/* Checkbox native onFocus and onBlur events messing up react-select value selection [T20-22340] */}
        <div className={cn(optionContentClassName)}>
          {/* eslint-disable-next-line no-nested-ternary -- Pre-existing violation */}
          {hasOptionCheckbox ? (
            <HStack spacing={3}>
              <Checkbox
                onChange={noop}
                value={isSelected}
                isDisabled={isDisabled}
                data-testid={suffixTestId('option-checkbox', context)}
              />
              {/* @ts-ignore Children is typed as node by react-select - cant change that */}
              <Text size="small">{children}</Text>
              <Show when={isNotNil(statusIndicator)}>
                <StatusIndicatorDot statusIndicator={statusIndicator} />
              </Show>
            </HStack>
          ) : statusIndicator ? (
            <HStack spacing={2} align="center">
              {/* @ts-ignore Children is typed as node by react-select - cant change that */}
              <Text size="small">{children}</Text>
              <StatusIndicatorDot statusIndicator={statusIndicator} />
            </HStack>
          ) : (
            children
          )}
        </div>
      </div>
    );
  },
  Input: ChoiceInput,
  MultiValue: <
    Option extends OptionTypeBase<ValueType | Nullish>,
    IsMultiValue extends boolean,
    Group extends GroupBase<Option>,
  >(
    props: MultiValueProps<Option, IsMultiValue, Group>
  ) => {
    const {children, components, data, innerProps, isFocused, selectProps, removeProps} = props;

    const {Container} = components;

    const context = useChoice();

    const statusIndicator = data.statusIndicator;

    return (
      <div className="eag--choice__multi-value">
        <Container data={data} innerProps={innerProps} selectProps={selectProps}>
          <Text
            data-testid={suffixTestId('choiceMultiValueLabel', context)}
            size="xSmall"
            color="white"
            overflowWrap="anywhere"
          >
            {children}
          </Text>
          <Show when={isNotNil(statusIndicator)}>
            <StatusIndicatorDot statusIndicator={statusIndicator} />
          </Show>
          <Box
            borderColor={isFocused ? 'palettes.blue.40.100' : undefined}
            border={isFocused ? '2px solid' : undefined}
          >
            <div
              {...removeProps}
              onClick={(event: MouseEvent<HTMLDivElement>) =>
                handleMultiValueRemoveClick(event, selectProps.isDisabled, removeProps?.onClick)
              }
              className={cn(multiValueRemoveVariants({isDisabled: selectProps.isDisabled}))}
              data-testid={suffixTestId('choiceMultiValueRemoveContainer', context)}
            >
              <Center height={3} width={4}>
                <Icon
                  size={3}
                  color="general.white"
                  value="navigation/close"
                  data-testid={suffixTestId('choiceMultiValueRemove', context)}
                />
              </Center>
            </div>
          </Box>
        </Container>
      </div>
    );
  },
  SelectContainer: function SelectContainer(props) {
    const context = useChoice();
    return (
      <reactSelectComponents.SelectContainer
        data-testid={suffixTestId('choiceComponentContainer', context)}
        {...props}
        className={props.className}
      />
    );
  },
  SingleValue: function SingleValue(
    props: SingleValueProps<OptionTypeBase<ValueType | Nullish>, IsMulti>
  ) {
    const statusIndicator = props.data.statusIndicator;

    return (
      <reactSelectComponents.SingleValue
        {...props}
        className={`${props.className} eag--choice__single-value`}
      >
        {statusIndicator ? (
          <HStack spacing={2} align="center">
            {props.children}
            <StatusIndicatorDot statusIndicator={statusIndicator} />
          </HStack>
        ) : (
          props.children
        )}
      </reactSelectComponents.SingleValue>
    );
  },
});

export function wrapWithClassName<Props extends {className?: string}>(
  Base: ComponentType<Props>,
  className: string
  // eslint-disable-next-line -eslint/no-explicit-any
): ComponentType<any> {
  const Wrapped = (props: Props) => (
    <Base {...props} className={`${props.className} ${className}`} />
  );

  return Wrapped;
}
